import { motion } from "framer-motion";
import { ABOUT_CONTENT, ABOUT_IMAGES } from "../data/siteContent";

function Stars() {
  return (
    <div className="flex gap-0.5" aria-hidden>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} className="h-4 w-4 text-amber-400 sm:h-[18px] sm:w-[18px]" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function PerformanceCard({ data }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.35, duration: 0.6 }}
      className="absolute -bottom-[5.5rem] right-2 z-20 w-[min(100%,300px)] rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.25)] sm:right-4 lg:right-0"
    >
      <div className="mb-4 flex items-center justify-between">
        <span className="font-sans text-sm font-semibold text-slate-800">{data.title}</span>
        <button type="button" className="text-slate-400" aria-label="More options">
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {data.metrics.map((m) => (
          <div key={m.label} className="flex gap-3">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                m.color === "blue" ? "bg-sky-100 text-sky-600" : "bg-orange-100 text-orange-500"
              }`}
            >
              {m.color === "blue" ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 8h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
                </svg>
              )}
            </span>
            <div>
              <p className="flex items-center gap-1 font-sans text-lg font-bold text-slate-900">
                {m.value}
                <span className={m.trend === "up" ? "text-emerald-500" : "text-orange-500"}>
                  {m.trend === "up" ? "↑" : "↓"}
                </span>
              </p>
              <p className="text-xs text-slate-500">{m.label}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5">
        <div className="mb-2 flex justify-between text-xs text-slate-600">
          <span>{data.progress.label}</span>
          <span className="font-semibold text-slate-800">{data.progress.percent}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: `${data.progress.percent}%` }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.9 }}
            className="h-full rounded-full bg-gradient-to-r from-sky-500 to-indigo-500"
          />
        </div>
      </div>
    </motion.div>
  );
}

export default function About() {
  const { eyebrow, headline, performance, reviews, cta } = ABOUT_CONTENT;

  return (
    <section id="about" className="relative overflow-hidden bg-white py-20 text-slate-900 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16 xl:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="relative mx-auto w-full max-w-xl lg:max-w-none"
          >
            <motion.div className="relative pb-8 sm:pb-12">
              <img
                src={ABOUT_IMAGES.main}
                alt="Our expertise in creative advertising"
                className="relative z-10 w-full rounded-3xl object-cover shadow-[0_24px_60px_-20px_rgba(15,23,42,0.2)]"
                style={{ aspectRatio: "16/11" }}
              />
              <PerformanceCard data={performance} />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:pl-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-5 flex items-center gap-2"
            >
              <span className="flex h-2.5 w-2.5 rounded-sm bg-gradient-to-br from-cyan-500 to-indigo-500" />
              <span className="font-sans text-sm font-medium text-slate-600">{eyebrow}</span>
            </motion.div>

            <h2 className="font-sans text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-[2.65rem] lg:leading-[1.15]">
              {headline}
            </h2>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="mt-10 grid gap-8 sm:grid-cols-2 sm:gap-6"
            >
              {reviews.map((item, i) => (
                <motion.div
                  key={item.rating}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.25 + i * 0.1 }}
                >
                  <p className="font-sans text-4xl font-bold text-slate-900 sm:text-[2.75rem]">{item.rating}</p>
                  <div className="mt-2">
                    <Stars />
                  </div>
                  <span className="mt-4 inline-block rounded-full bg-gradient-to-r from-violet-100 via-indigo-50 to-sky-100 px-4 py-1.5 font-sans text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                    Review Customer
                  </span>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-slate-500">{item.text}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.a
              href={cta.href}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.45 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-10 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-blue-500 to-cyan-400 px-10 py-4 font-sans text-sm font-semibold text-white shadow-[0_12px_32px_-8px_rgba(14,165,233,0.55)] transition hover:shadow-[0_16px_40px_-8px_rgba(14,165,233,0.65)]"
            >
              {cta.label}
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
